import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Row,
  Col,
  Card,
  Statistic,
  DatePicker,
  Select,
  Spin,
  Typography,
  Table,
  Tag,
  Tooltip as AntTooltip,
  Badge,
} from "antd";
import {
  UserOutlined,
  WifiOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  QuestionCircleOutlined,
  ApiOutlined,
  TeamOutlined,
  CrownOutlined,
  FireOutlined,
  RiseOutlined,
  FallOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import moment from "moment";
import {
  getRealtimeStatsRequest,
  getHourlyStatsRequest,
  getDailyStatsRequest,
  getMonthlyStatsRequest,
  getPeakHoursRequest,
  getHeatmapStatsRequest,
  getOnlineUsersRequest,
} from "providers/UserActivityProvider/slice";
import "./styles.less";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const HOUR_LABELS = [
  "00:00","01:00","02:00","03:00","04:00","05:00",
  "06:00","07:00","08:00","09:00","10:00","11:00",
  "12:00","13:00","14:00","15:00","16:00","17:00",
  "18:00","19:00","20:00","21:00","22:00","23:00",
];

const DAY_LABELS = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // T2->CN

const ROLE_MAP = {
  super_admin: { label: "Super Admin", color: "red" },
  admin: { label: "Admin", color: "volcano" },
  manager: { label: "Manager", color: "orange" },
  user: { label: "User", color: "blue" },
};

const TIME_PERIODS = [
  { name: "Đêm khuya", hours: [0,1,2,3,4], color: "#722ed1" },
  { name: "Sáng sớm", hours: [5,6,7,8], color: "#fa8c16" },
  { name: "Buổi trưa", hours: [9,10,11,12,13], color: "#52c41a" },
  { name: "Buổi chiều", hours: [14,15,16,17], color: "#1890ff" },
  { name: "Buổi tối", hours: [18,19,20,21,22,23], color: "#f5222d" },
];

const HEATMAP_COLORS = [
  "#f0f5ff", "#bae7ff", "#91d5ff", "#69c0ff",
  "#40a9ff", "#1890ff", "#096dd9", "#0050b3",
  "#003a8c", "#002766",
];

const getHeatColor = (value, maxVal) => {
  if (!maxVal || value === 0) return HEATMAP_COLORS[0];
  const idx = Math.min(Math.floor((value / maxVal) * (HEATMAP_COLORS.length - 1)), HEATMAP_COLORS.length - 1);
  return HEATMAP_COLORS[idx];
};

const LabelWithTip = ({ label, tip }) => (
  <span>
    {label}{" "}
    <AntTooltip title={tip}>
      <QuestionCircleOutlined style={{ color: "#999", fontSize: 13 }} />
    </AntTooltip>
  </span>
);

const formatLastSeen = (lastSeen) => {
  if (!lastSeen) return <Text type="secondary">—</Text>;
  const diff = moment().diff(moment(lastSeen), "minutes");
  if (diff < 1) return <Tag color="green">Vừa mới</Tag>;
  if (diff < 60) return <Tag color="blue">{diff} phút trước</Tag>;
  const hours = Math.floor(diff / 60);
  if (hours < 24) return <Tag>{hours} giờ trước</Tag>;
  return <Tag>{moment(lastSeen).format("DD/MM HH:mm")}</Tag>;
};

// ======= Heatmap Component =======
const HeatmapChart = ({ data }) => {
  const [tooltip, setTooltip] = useState(null);

  const { grid, maxVal } = useMemo(() => {
    const g = {};
    let mx = 0;
    (data || []).forEach((d) => {
      const key = `${d.dayOfWeek}-${d.hour}`;
      g[key] = d;
      if (d.avgOnlineCount > mx) mx = d.avgOnlineCount;
    });
    return { grid: g, maxVal: mx };
  }, [data]);

  return (
    <div className="heatmap-container">
      <div className="heatmap-grid">
        <div className="heatmap-header"></div>
        {HOUR_LABELS.map((h, i) => (
          <div key={i} className="heatmap-header">{i}</div>
        ))}

        {DAY_ORDER.map((dow) => (
          <React.Fragment key={dow}>
            <div className="heatmap-day-label">{DAY_LABELS[dow]}</div>
            {Array.from({ length: 24 }, (_, hour) => {
              const cell = grid[`${dow}-${hour}`];
              const val = cell ? cell.avgOnlineCount : 0;
              return (
                <div
                  key={hour}
                  className="heatmap-cell"
                  style={{ backgroundColor: getHeatColor(val, maxVal) }}
                  onMouseEnter={(e) => {
                    setTooltip({
                      x: e.clientX + 10,
                      y: e.clientY - 40,
                      day: DAY_LABELS[dow],
                      hour: HOUR_LABELS[hour],
                      avg: val,
                      max: cell ? cell.maxOnlineCount : 0,
                      samples: cell ? cell.sampleCount : 0,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {Math.round(val) || ""}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className="heatmap-legend">
        <span>Ít</span>
        <div className="legend-bar">
          {HEATMAP_COLORS.map((c, i) => (
            <div key={i} className="legend-color" style={{ backgroundColor: c }} />
          ))}
        </div>
        <span>Nhiều</span>
      </div>

      {tooltip && (
        <div className="heatmap-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="ht-title">{tooltip.day}, {tooltip.hour}</div>
          <div>TB: <span className="ht-value">{tooltip.avg}</span> người</div>
          <div>Cao nhất: {tooltip.max} | Mẫu: {tooltip.samples}</div>
        </div>
      )}
    </div>
  );
};

// ======= Main Component =======
const UserActivity = () => {
  const dispatch = useDispatch();
  const {
    isLoading,
    realtimeStats,
    hourlyStats,
    dailyStats,
    monthlyStats,
    peakHours,
    heatmapStats,
    onlineAdmins,
    onlineUsers,
    onlineAdminTotal,
    onlineUserTotal,
  } = useSelector((state) => state.userActivity);

  const [viewMode, setViewMode] = useState("hourly");
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);
  const [selectedYear, setSelectedYear] = useState(moment().year());

  const fetchData = useCallback(() => {
    const startDate = dateRange[0].format("YYYY-MM-DD");
    const endDate = dateRange[1].format("YYYY-MM-DD");

    dispatch(getRealtimeStatsRequest());
    dispatch(getOnlineUsersRequest());

    if (viewMode === "hourly") {
      dispatch(getHourlyStatsRequest({ startDate, endDate }));
      dispatch(getPeakHoursRequest({ startDate, endDate, limit: 10 }));
      dispatch(getHeatmapStatsRequest({ startDate, endDate }));
    } else if (viewMode === "daily") {
      dispatch(getDailyStatsRequest({ startDate, endDate }));
    } else if (viewMode === "monthly") {
      dispatch(getMonthlyStatsRequest({ year: selectedYear }));
    }
  }, [dispatch, viewMode, dateRange, selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getRealtimeStatsRequest());
      dispatch(getOnlineUsersRequest());
    }, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const onDateRangeChange = (dates) => {
    if (dates && dates.length === 2) setDateRange(dates);
  };

  // ======= Computed data for insights =======
  const insights = useMemo(() => {
    if (!hourlyStats || hourlyStats.length === 0) return null;
    const sorted = [...hourlyStats].sort((a, b) => b.avgOnlineCount - a.avgOnlineCount);
    const peak = sorted[0];
    const low = sorted[sorted.length - 1];
    const ratio = low.avgOnlineCount > 0 ? (peak.avgOnlineCount / low.avgOnlineCount).toFixed(1) : "N/A";

    // Period analysis
    const periodData = TIME_PERIODS.map((p) => {
      const periodStats = hourlyStats.filter((h) => p.hours.includes(h.hour));
      const totalAvg = periodStats.length > 0
        ? periodStats.reduce((s, h) => s + h.avgOnlineCount, 0) / periodStats.length
        : 0;
      const maxInPeriod = periodStats.length > 0
        ? Math.max(...periodStats.map((h) => h.maxOnlineCount))
        : 0;
      return { ...p, avg: Math.round(totalAvg * 10) / 10, max: maxInPeriod };
    });
    const busiest = periodData.reduce((a, b) => (a.avg > b.avg ? a : b));

    return { peak, low, ratio, periodData, busiest };
  }, [hourlyStats]);

  // Radar chart data
  const radarData = useMemo(() => {
    return HOUR_LABELS.map((label, idx) => {
      const stat = hourlyStats.find((s) => s.hour === idx);
      return {
        hour: label,
        avg: stat ? stat.avgOnlineCount : 0,
        max: stat ? stat.maxOnlineCount : 0,
      };
    });
  }, [hourlyStats]);

  const dailyChartData = dailyStats.map((s) => ({
    date: s.date,
    "Trung bình": s.avgOnlineCount,
    "Cao nhất": s.maxOnlineCount,
    "Thấp nhất": s.minOnlineCount,
  }));

  const monthlyChartData = monthlyStats.map((s) => ({
    month: s.month,
    "Trung bình": s.avgOnlineCount,
    "Cao nhất": s.maxOnlineCount,
    "Số ngày có dữ liệu": s.daysWithData,
  }));

  const DailyChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="chart-custom-tooltip">
        <p className="tooltip-title">Ngày: {label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color, margin: "4px 0" }}>
            {entry.name}: <strong>{entry.value}</strong> người
          </p>
        ))}
      </div>
    );
  };

  const MonthlyChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="chart-custom-tooltip">
        <p className="tooltip-title">Tháng: {label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color, margin: "4px 0" }}>
            {entry.name}: <strong>{entry.value}</strong>
            {entry.name === "Số ngày có dữ liệu" ? " ngày" : " người"}
          </p>
        ))}
      </div>
    );
  };

  // ======= Admin / User columns =======
  const adminColumns = [
    { title: "STT", key: "index", width: 50, render: (_, __, i) => i + 1 },
    { title: "Tên", dataIndex: "name", key: "name", render: (n) => <strong>{n || "—"}</strong> },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    { title: "SĐT", dataIndex: "phone", key: "phone", width: 130, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: "Vai trò", dataIndex: "role", key: "role", width: 120,
      render: (role, rec) => {
        if (rec.roleChat) return <Tag color="purple">Chat Support</Tag>;
        const info = ROLE_MAP[role] || { label: role, color: "default" };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: <LabelWithTip label="Hoạt động" tip="Thời điểm cuối cùng hệ thống ghi nhận người dùng này hoạt động" />,
      dataIndex: "lastSeen", key: "lastSeen", width: 130, render: formatLastSeen,
    },
  ];

  const userColumns = [
    { title: "STT", key: "index", width: 50, render: (_, __, i) => i + 1 },
    { title: "Tên", dataIndex: "name", key: "name", render: (n) => <strong>{n || "—"}</strong> },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    { title: "SĐT", dataIndex: "phone", key: "phone", width: 130, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: <LabelWithTip label="Hoạt động" tip="Thời điểm cuối cùng hệ thống ghi nhận người dùng này hoạt động" />,
      dataIndex: "lastSeen", key: "lastSeen", width: 130, render: formatLastSeen,
    },
  ];

  const peakHoursColumns = [
    { title: "Hạng", key: "rank", width: 60, render: (_, __, i) => <Tag color={i < 3 ? "red" : "default"}>{i + 1}</Tag> },
    {
      title: <LabelWithTip label="Khung giờ" tip="Khung giờ trong ngày (0h – 23h) được xếp hạng theo số người dùng trung bình" />,
      dataIndex: "hour", key: "hour", render: (h) => <strong>{HOUR_LABELS[h]}</strong>,
    },
    {
      title: "TB online", dataIndex: "avgOnlineCount", key: "avg",
      render: (v) => <span style={{ color: "#1890ff", fontWeight: 600 }}>{v}</span>,
    },
    { title: "Cao nhất", dataIndex: "maxOnlineCount", key: "max" },
    { title: "Mẫu", dataIndex: "sampleCount", key: "sc", render: (v) => <Tag>{v}</Tag> },
  ];

  // Max period avg for bar width calculation
  const maxPeriodAvg = insights ? Math.max(...insights.periodData.map((p) => p.avg), 1) : 1;

  return (
    <div className="user-activity-wrapper">
      <Title level={3}>
        <ThunderboltOutlined /> Thống kê hoạt động người dùng
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
        Theo dõi số lượng người dùng đang sử dụng ứng dụng theo thời gian thực và phân tích khung giờ cao điểm. Dữ liệu được hệ thống tự động ghi nhận mỗi 5 phút.
      </Text>

      {/* Realtime stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card className="stat-card stat-card-online">
            <Statistic
              title={<LabelWithTip label="Đang online" tip="Số người dùng có trạng thái online trong CSDL." />}
              value={realtimeStats?.onlineCount ?? "-"}
              prefix={<WifiOutlined />}
              valueStyle={{ color: "#52c41a" }}
              suffix="người"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card stat-card-socket">
            <Statistic
              title={<LabelWithTip label="Kết nối Socket" tip="Số kết nối WebSocket đang hoạt động." />}
              value={realtimeStats?.socketCount ?? "-"}
              prefix={<ApiOutlined />}
              valueStyle={{ color: "#1890ff" }}
              suffix="kết nối"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card stat-card-unique">
            <Statistic
              title={<LabelWithTip label="Người dùng kết nối" tip="Số người dùng duy nhất đang kết nối WebSocket." />}
              value={realtimeStats?.uniqueConnectedUsers ?? "-"}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#722ed1" }}
              suffix="người"
            />
          </Card>
        </Col>
      </Row>

      {/* Online users tables */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={<LabelWithTip label={<><CrownOutlined style={{ color: "#fa541c" }} /> Admin đang online <Badge count={onlineAdminTotal} style={{ backgroundColor: "#fa541c", marginLeft: 8 }} /></>} tip="Danh sách admin, super admin và manager đang online." />}
            bodyStyle={{ padding: 0 }}
          >
            <Table columns={adminColumns} dataSource={(onlineAdmins || []).map((u, i) => ({ ...u, key: u._id || i }))} pagination={onlineAdminTotal > 10 ? { pageSize: 10, size: "small" } : false} size="small" scroll={{ x: 600 }} locale={{ emptyText: "Không có admin nào đang online" }} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<LabelWithTip label={<><TeamOutlined style={{ color: "#1890ff" }} /> Người dùng đang online <Badge count={onlineUserTotal} style={{ backgroundColor: "#1890ff", marginLeft: 8 }} overflowCount={9999} /></>} tip="Danh sách người dùng thường đang online." />}
            bodyStyle={{ padding: 0 }}
          >
            <Table columns={userColumns} dataSource={(onlineUsers || []).map((u, i) => ({ ...u, key: u._id || i }))} pagination={onlineUserTotal > 10 ? { pageSize: 10, size: "small" } : false} size="small" scroll={{ x: 500 }} locale={{ emptyText: "Không có người dùng nào đang online" }} />
          </Card>
        </Col>
      </Row>

      {/* Filter bar */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <span style={{ marginRight: 8, fontWeight: 500 }}>Xem theo:</span>
            <Select value={viewMode} onChange={setViewMode} style={{ width: 150 }}>
              <Option value="hourly">Theo giờ</Option>
              <Option value="daily">Theo ngày</Option>
              <Option value="monthly">Theo tháng</Option>
            </Select>
          </Col>
          <Col>
            {viewMode !== "monthly" ? (
              <>
                <span style={{ marginRight: 8, fontWeight: 500 }}>Khoảng thời gian:</span>
                <RangePicker value={dateRange} onChange={onDateRangeChange} format="DD/MM/YYYY" placeholder={["Từ ngày", "Đến ngày"]} />
              </>
            ) : (
              <>
                <span style={{ marginRight: 8, fontWeight: 500 }}>Năm:</span>
                <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 120 }}>
                  {[...Array(5)].map((_, i) => { const y = moment().year() - i; return <Option key={y} value={y}>Năm {y}</Option>; })}
                </Select>
              </>
            )}
          </Col>
          <Col>
            <a onClick={fetchData} style={{ cursor: "pointer" }}>
              <ReloadOutlined /> Làm mới
            </a>
          </Col>
        </Row>
      </Card>

      <Spin spinning={isLoading}>
        {/* ========= HOURLY VIEW (REDESIGNED) ========= */}
        {viewMode === "hourly" && (
          <>
            {/* Insight Cards */}
            {insights && (
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                  <Card className="insight-card" style={{ borderTop: "3px solid #f5222d" }}>
                    <div className="insight-icon"><FireOutlined style={{ color: "#f5222d" }} /></div>
                    <div className="insight-label">Giờ cao điểm nhất</div>
                    <div className="insight-value" style={{ color: "#f5222d" }}>{HOUR_LABELS[insights.peak.hour]}</div>
                    <div className="insight-sub">TB {insights.peak.avgOnlineCount} người</div>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="insight-card" style={{ borderTop: "3px solid #52c41a" }}>
                    <div className="insight-icon"><RiseOutlined style={{ color: "#52c41a" }} /></div>
                    <div className="insight-label">Buổi đông nhất</div>
                    <div className="insight-value" style={{ color: "#52c41a" }}>{insights.busiest.name}</div>
                    <div className="insight-sub">TB {insights.busiest.avg} người</div>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="insight-card" style={{ borderTop: "3px solid #1890ff" }}>
                    <div className="insight-icon"><FallOutlined style={{ color: "#1890ff" }} /></div>
                    <div className="insight-label">Giờ thấp điểm</div>
                    <div className="insight-value" style={{ color: "#1890ff" }}>{HOUR_LABELS[insights.low.hour]}</div>
                    <div className="insight-sub">TB {insights.low.avgOnlineCount} người</div>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="insight-card" style={{ borderTop: "3px solid #722ed1" }}>
                    <div className="insight-icon"><SwapOutlined style={{ color: "#722ed1" }} /></div>
                    <div className="insight-label">Chênh lệch cao/thấp</div>
                    <div className="insight-value" style={{ color: "#722ed1" }}>{insights.ratio}x</div>
                    <div className="insight-sub">Cao điểm gấp {insights.ratio} lần thấp điểm</div>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Heatmap */}
            <Card
              title={<LabelWithTip label="Bản đồ nhiệt: Giờ × Ngày trong tuần" tip="Màu càng đậm = càng nhiều người dùng online. Giúp nhận ra pattern: ngày nào, giờ nào đông nhất." />}
              style={{ marginBottom: 16 }}
            >
              <HeatmapChart data={heatmapStats} />
            </Card>

            {/* Radar + Period Analysis */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={12}>
                <Card
                  title={<LabelWithTip label="Phân bố 24 giờ (Radar)" tip="Biểu đồ radar dạng đồng hồ. Vùng phình ra = giờ có nhiều người dùng nhất. Giúp nhận diện nhanh các vùng cao điểm." />}
                >
                  <ResponsiveContainer width="100%" height={360}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="hour" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis tick={{ fontSize: 10 }} />
                      <Radar name="Trung bình" dataKey="avg" stroke="#1890ff" fill="#1890ff" fillOpacity={0.3} />
                      <Radar name="Cao nhất" dataKey="max" stroke="#ff7a45" fill="#ff7a45" fillOpacity={0.1} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  title={<LabelWithTip label="Phân tích theo buổi" tip="So sánh số người dùng trung bình giữa các buổi trong ngày. Thanh càng dài = càng đông." />}
                  bodyStyle={{ padding: "12px 0" }}
                >
                  {insights && insights.periodData.map((p, i) => (
                    <div key={i} className="period-item">
                      <div className="period-name" style={{ color: p.color }}>{p.name}</div>
                      <div className="period-bar-wrap">
                        <div
                          className="period-bar"
                          style={{
                            width: `${Math.max((p.avg / maxPeriodAvg) * 100, 5)}%`,
                            backgroundColor: p.color,
                          }}
                        >
                          {p.avg}
                        </div>
                      </div>
                      <div className="period-stats">max {p.max}</div>
                    </div>
                  ))}
                </Card>
              </Col>
            </Row>

            {/* Peak hours table (compact) */}
            <Card
              title={<LabelWithTip label={<><ClockCircleOutlined /> Top khung giờ cao điểm</>} tip="Xếp hạng các khung giờ có số người dùng TB cao nhất." />}
            >
              <Table columns={peakHoursColumns} dataSource={peakHours.map((p, i) => ({ ...p, key: i }))} pagination={false} size="small" />
            </Card>
          </>
        )}

        {/* ========= DAILY VIEW ========= */}
        {viewMode === "daily" && (
          <Card
            title={<LabelWithTip label="Xu hướng người dùng online theo ngày" tip="Biểu đồ đường thể hiện số người dùng online trung bình, cao nhất và thấp nhất mỗi ngày." />}
          >
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={dailyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis label={{ value: "Số người", angle: -90, position: "insideLeft", style: { fontSize: 12 } }} />
                <Tooltip content={<DailyChartTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="Trung bình" stroke="#1890ff" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Cao nhất" stroke="#ff7a45" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Thấp nhất" stroke="#52c41a" strokeWidth={1} strokeDasharray="5 5" dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* ========= MONTHLY VIEW ========= */}
        {viewMode === "monthly" && (
          <Card
            title={<LabelWithTip label="Thống kê người dùng online theo tháng" tip="Biểu đồ cột thể hiện số người dùng online trung bình và cao nhất mỗi tháng." />}
          >
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis label={{ value: "Số người", angle: -90, position: "insideLeft", style: { fontSize: 12 } }} />
                <Tooltip content={<MonthlyChartTooltip />} />
                <Legend />
                <Bar dataKey="Trung bình" fill="#1890ff" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Cao nhất" fill="#ff7a45" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default UserActivity;
